import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const MU_ICON_URL =
  'https://customer-assets.emergentagent.com/job_career-heat-map/artifacts/9tou0chz_Untitled%20design%20%2813%29.png';

const ESTIMATE_MS  = 26000;
const FAST_END_PCT = 82;
// Progress % at which each step turns green. Last = Infinity → never completes.
const STEP_THRESHOLDS = [9, 22, 38, 53, 68, Infinity];

const loadingSteps = [
  'Reading your resume',
  'Identifying your strengths and experience',
  'Mapping your best-fit career paths',
  'Estimating your earning potential',
  'Building your personalised roadmap',
  'Finalising your report',
];

export const AnalysisLoading = () => {
  // React state only for step indicators (infrequent updates)
  const [activeIdx, setActiveIdx] = useState(0);
  const [doneUpTo,  setDoneUpTo]  = useState(-1);
  const [dots,      setDots]      = useState('');

  // Refs for smooth 60fps bar (bypasses React batching)
  const barRef      = useRef(null);
  const rafRef      = useRef(null);
  const startMs     = useRef(Date.now());
  const prevDoneRef = useRef(-1);

  // ── 60fps progress bar via direct DOM write ─────────────────────────────────
  useEffect(() => {
    const tick = () => {
      const elapsed = Date.now() - startMs.current;
      const ratio   = elapsed / ESTIMATE_MS;

      let pct;
      if (ratio <= 1) {
        pct = ratio * FAST_END_PCT;
      } else {
        // Slow crawl: asymptotically approaches 99 after expected time passes
        pct = FAST_END_PCT + (99 - FAST_END_PCT) * (1 - Math.exp(-(ratio - 1) * 0.55));
      }
      pct = Math.min(pct, 99);

      // Direct DOM write — no React re-render cost
      if (barRef.current) barRef.current.style.width = `${pct}%`;

      // React state update ONLY when a threshold is crossed (rare)
      let done = -1;
      for (let i = 0; i < STEP_THRESHOLDS.length; i++) {
        if (pct >= STEP_THRESHOLDS[i]) done = i;
      }
      if (done !== prevDoneRef.current) {
        prevDoneRef.current = done;
        setDoneUpTo(done);
        setActiveIdx(Math.min(done + 1, loadingSteps.length - 1));
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // ── Animated dots ───────────────────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 480);
    return () => clearInterval(id);
  }, []);

  return (
    <motion.div
      data-testid="analysis-loading"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.3 }}
      style={{ padding: '8px 0' }}
    >
      {/* Spinning emblem */}
      <div style={{ marginBottom: '24px', textAlign: 'center' }}>
        <div style={{ width: '64px', height: '64px', margin: '0 auto 20px', position: 'relative' }}>
          <svg viewBox="0 0 64 64" style={{ position: 'absolute', inset: 0, animation: 'spin 3s linear infinite' }}>
            <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(232,160,32,0.18)" strokeWidth="1.5" />
            <circle cx="32" cy="32" r="28" fill="none" stroke="#E8A020"
              strokeWidth="1.5" strokeDasharray="44 132" strokeLinecap="round" />
          </svg>
          <div style={{
            position: 'absolute', inset: '10px', backgroundColor: '#E8A020',
            borderRadius: '50%', display: 'flex', alignItems: 'center',
            justifyContent: 'center', overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(232,160,32,0.30)',
          }}>
            <img src={MU_ICON_URL} alt="MU" style={{
              width: '26px', height: '26px', objectFit: 'contain',
              filter: 'brightness(0) invert(1)', opacity: 0.95,
            }} />
          </div>
        </div>

        <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '18px', fontWeight: 700, color: '#111111', marginBottom: '5px' }}>
          Analysing your profile
        </h3>
        <p data-testid="analysis-loading-status-text"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(0,0,0,0.45)' }}>
          {loadingSteps[activeIdx]}{dots}
        </p>
      </div>

      {/* ── Progress bar — direct DOM ref, 60fps, no React state ── */}
      <div style={{
        height: '3px', backgroundColor: '#eeeeee', borderRadius: '9999px',
        overflow: 'hidden', marginBottom: '24px',
      }}>
        <div
          ref={barRef}
          style={{
            height: '100%',
            width: '0%',                                  /* starts at 0, RAF writes directly */
            background: 'linear-gradient(90deg, #8EE5B4, #5BBF80, #3FA86A)',
            borderRadius: '9999px',
          }}
        />
      </div>

      {/* Steps list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {loadingSteps.map((label, i) => {
          const isCompleted = i <= doneUpTo;  // last step (i=5) never doneUpTo >= 5
          const isActive    = i === activeIdx && !isCompleted;

          return (
            <motion.div
              key={label}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: isCompleted || isActive ? 1 : 0.28, x: 0 }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '8px 12px',
                backgroundColor: isActive ? 'rgba(232,160,32,0.06)' : 'transparent',
                border: isActive ? '1px solid rgba(232,160,32,0.18)' : '1px solid transparent',
                borderRadius: '8px',
                transition: 'background-color 0.3s ease, border-color 0.3s ease',
              }}
            >
              {/* Status indicator */}
              <div style={{
                width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                backgroundColor: isCompleted ? '#3FA86A' : isActive ? 'rgba(232,160,32,0.15)' : '#f0f0f0',
                border: `1.5px solid ${isCompleted ? '#3FA86A' : isActive ? '#E8A020' : 'rgba(0,0,0,0.10)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.3s ease',
              }}>
                {isCompleted ? (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5l2.5 2.5L8 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : isActive ? (
                  <div style={{
                    width: '6px', height: '6px', borderRadius: '50%',
                    backgroundColor: '#E8A020',
                    animation: 'pulse 1s ease-in-out infinite',
                  }} />
                ) : null}
              </div>

              {/* Label */}
              <span style={{
                fontFamily: 'DM Sans, sans-serif', fontSize: '14px',
                color: isCompleted ? 'rgba(0,0,0,0.60)' : isActive ? '#111111' : 'rgba(0,0,0,0.28)',
                fontWeight: isActive ? 500 : 400,
                transition: 'color 0.3s ease',
              }}>
                {label}
              </span>

              {/* Shimmer on active step */}
              {isActive && (
                <div style={{ flex: 1, height: '2px', backgroundColor: '#eeeeee', borderRadius: '9999px', overflow: 'hidden' }}>
                  <div className="shimmer" style={{ width: '100%', height: '100%' }} />
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      <p style={{
        fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px',
        color: 'rgba(0,0,0,0.28)', marginTop: '20px', textAlign: 'center',
      }}>
        Powered by AI · Takes about 30–60 seconds
      </p>
    </motion.div>
  );
};
